import { Button } from "@/components/ui/button";
import { useBackOfficeAccess } from "@/features/auth/back-office-access";
import {
  useMyClients,
  usePendingInvitations,
} from "@/features/clients/hooks/use-clients";
import { landingCompany } from "@/features/clients/hooks/use-preferred-company";
import { useTranslation } from "@/features/utils/i18n";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { BuildingApartmentIcon, PlusIcon } from "@phosphor-icons/react";
import { Navigate, useNavigate } from "react-router-dom";
import { CompaniesLayout } from "./layout";

/**
 * Where a signed-in user with no company lands — a signpost, nothing more.
 *
 * It creates nothing itself: an invitation waiting for them is the reason they
 * signed in, so they are taken to it, and opening a company is the back-office
 * companies page's job (which also runs the onboarding after). Signing up is
 * the third way in, and it names the company on its own form.
 */
export function NoCompanyLanding() {
  const { t } = useTranslation("start");
  const navigate = useNavigate();

  const { data: clients, isLoading } = useMyClients();
  const access = useBackOfficeAccess();
  const { data: invitations = [], isLoading: isLoadingInvitations } =
    usePendingInvitations();

  // Only for a user with no company: one who has some is sent into it below,
  // and two redirects racing would let this one win.
  useControlledEffect(() => {
    if (isLoading || clients?.length) return;
    if (isLoadingInvitations || !invitations.length) return;
    navigate("/invitations", { replace: true });
  }, [isLoading, clients?.length, isLoadingInvitations, invitations.length]);

  if (isLoading) {
    return null;
  }

  // A user with companies has nothing to do here: open the last one they had
  // open, else the first one. As an
  // element, not a `navigate()` call — navigating during render sets the
  // router's state while this component is still rendering.
  if (clients && clients.length > 0) {
    return (
      <Navigate to={`/${landingCompany(clients)!.client.id}/home`} replace />
    );
  }

  // A read-only back-office account (sales) with no company of its own is here
  // for the back-office, not to create a company.
  if (access === "read") {
    return <Navigate to="/admin/live" replace />;
  }

  return (
    <CompaniesLayout
      title={t("welcome.title")}
      description={t("welcome.description")}
    >
      {/* An invitation is being redirected to; anything drawn here would only
          flash by. */}
      {isLoadingInvitations || invitations.length > 0 ? (
        <div className="text-center text-muted-foreground">
          {t("invitations.loading")}
        </div>
      ) : (
        <div className="text-center py-8 max-w-sm mx-auto">
          <BuildingApartmentIcon
            className="h-12 w-12 mx-auto mb-4"
            weight="fill"
          />
          <Button onClick={() => navigate("/companies")}>
            <PlusIcon className="h-4 w-4" />
            {t("createCompany.button")}
          </Button>
          <p className="text-muted-foreground mt-4">
            {t("noCompany.description")}
          </p>
          <Button
            className="w-full text-center mt-4"
            variant={"link"}
            onClick={() => window.location.reload()}
          >
            {t("noCompany.expectingInvitation")}
          </Button>
        </div>
      )}
    </CompaniesLayout>
  );
}
